import React, { useEffect, useMemo, useState } from 'react';
import {
  getAllCompanies,
  createWebsiteFormSource,
  getWebsiteFormSources,
  updateWebsiteFormSource,
  getProjectsByCompanyId,
  getAllSalesUsersByProjectId
} from '../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import NoDataFound from '../components/layout/NoDataFound';
import Modal from '../../shared/components/ui/Modal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Multiselect from 'multiselect-react-dropdown';

const WebsiteForms = () => {
  const userId = Cookies.get('userId');
  const navigate = useNavigate();
  const { collapsed } = useSidebar();

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [newFormName, setNewFormName] = useState('');
  const [openMapModal, setOpenMapModal] = useState(false);
  const [sourceId, setSourceId] = useState(null);
  const [mapData, setMapData] = useState({ project_id: '', sales_ids: [] });
  const [selectedSalesPersons, setSelectedSalesPersons] = useState([]);

  useEffect(() => {
    mixpanel.track('Website Forms Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: getAllCompanies
  });

  useEffect(() => {
    if (!selectedCompanyId && Array.isArray(companies) && companies.length > 0) {
      setSelectedCompanyId(String(companies[0].id));
    }
  }, [companies, selectedCompanyId]);

  const {
    data: forms,
    refetch: refetchForms,
    isPending: isFormsPending
  } = useQuery({
    queryKey: ['websiteForms', selectedCompanyId],
    queryFn: () => getWebsiteFormSources(selectedCompanyId),
    enabled: !!selectedCompanyId
  });

  const { data: projects } = useQuery({
    queryKey: ['project_byId', selectedCompanyId],
    queryFn: () => getProjectsByCompanyId(selectedCompanyId),
    enabled: !!selectedCompanyId
  });

  const { data: sales } = useQuery({
    queryKey: ['salesUsers', mapData.project_id],
    queryFn: () => getAllSalesUsersByProjectId(mapData.project_id, 1, 500),
    enabled: !!mapData.project_id
  });

  const formList = useMemo(() => (Array.isArray(forms) ? forms : []), [forms]);

  const { mutate: createForm, isPending: isCreating } = useMutation({
    mutationFn: (body) => createWebsiteFormSource(body),
    onSuccess: async () => {
      toast.success('Website form created');
      setOpenCreateModal(false);
      setNewFormName('');
      await refetchForms();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to create form');
    }
  });

  const { mutate: saveForm, isPending: isSaving } = useMutation({
    mutationFn: ({ id, body }) => updateWebsiteFormSource(id, body),
    onSuccess: async () => {
      toast.success('Saved');
      setOpenMapModal(false);
      setSourceId(null);
      setMapData({ project_id: '', sales_ids: [] });
      setSelectedSalesPersons([]);
      await refetchForms();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save');
    }
  });

  const handleCreate = () => {
    if (!selectedCompanyId) {
      toast.error('Select a company first');
      return;
    }
    if (!newFormName.trim()) {
      toast.error('Enter a form name');
      return;
    }
    createForm({ company_id: parseInt(selectedCompanyId, 10), name: newFormName.trim() });
  };

  const handleShowMapModal = (form) => {
    setMapData({
      project_id: form.project_id ? String(form.project_id) : '',
      sales_ids: form.sales_persons?.map((p) => p.id) || []
    });
    setSelectedSalesPersons(form.sales_persons || []);
    setSourceId(form.id);
    setOpenMapModal(true);
  };

  const handleSubmitMap = () => {
    if (!mapData.project_id || !mapData.sales_ids?.length) {
      toast.error('Please select project and sales persons');
      return;
    }
    if (!sourceId) {
      return;
    }
    saveForm({
      id: sourceId,
      body: {
        project_id: parseInt(mapData.project_id, 10),
        sales_ids: mapData.sales_ids
      }
    });
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Webhook URL copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleToggleActive = (form) => {
    saveForm({ id: form.id, body: { is_active: !form.is_active } });
  };

  const handleRegenerate = (form) => {
    saveForm({ id: form.id, body: { regenerate_token: true } });
  };

  return (
    <Layout>
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          backArrow={true}
          onBack={() => navigate('/builder/integration')}
          data={{ name: 'Integrations > Website Forms' }}
        />
      </div>

      <div
        className={`flex z-20 items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Company</span>
          <select
            className="p-2 border-[1px] border-stone-300 rounded text-sm"
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}>
            <option value="" disabled>
              Select a company
            </option>
            {companies?.map((company) => (
              <option key={company.id} value={company.id}>
                {company.company_name}
              </option>
            ))}
          </select>
        </div>
        <Button onClick={() => setOpenCreateModal(true)} disabled={!selectedCompanyId}>
          Add Form
        </Button>
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="mt-[9rem] w-full h-full bg-gray-100 p-6">
          <div className="mb-6 rounded-lg border border-teal-200 bg-teal-50 p-4 text-sm text-teal-900">
            <p className="font-semibold mb-1">How it works</p>
            <p>
              Create a form, map it to a project, then paste its webhook URL into your website form
              (in Framer: Form → Send To → Webhook). Each submission with a phone number is added to
              the mapped prelead list automatically.
            </p>
          </div>

          {isFormsPending ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#346C75] border-t-transparent" />
            </div>
          ) : formList.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {formList.map((form) => (
                <div key={form.id} className="bg-white shadow-md p-6 rounded-lg flex flex-col">
                  <div className="flex justify-between items-start mb-2">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded ${
                        form.is_active
                          ? 'text-[#2A565E] bg-[#E8F4F3]'
                          : 'text-gray-500 bg-gray-100'
                      }`}>
                      {form.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {form.project_id ? (
                      <span className="text-xs text-[#418b7a] font-medium">Mapped</span>
                    ) : (
                      <span className="text-xs text-amber-600 font-medium">Unmapped</span>
                    )}
                  </div>
                  <h3 className="font-bold text-lg mb-2">{form.name}</h3>
                  {form.project?.name && (
                    <p className="text-sm text-gray-600 mb-1">Project: {form.project.name}</p>
                  )}
                  {form.pre_lead_list?.name && (
                    <p className="text-sm text-gray-600 mb-3">
                      Prelead list: {form.pre_lead_list.name}
                    </p>
                  )}

                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Webhook URL</p>
                    <div className="flex items-stretch gap-2">
                      <input
                        readOnly
                        value={form.webhook_url || ''}
                        className="flex-1 min-w-0 text-xs p-2 border border-stone-300 rounded bg-gray-50 truncate"
                      />
                      <button
                        type="button"
                        onClick={() => handleCopy(form.webhook_url)}
                        className="text-xs px-2 py-1 rounded bg-[#2A565E] text-white shrink-0">
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="mt-auto flex flex-wrap gap-2">
                    <Button onClick={() => handleShowMapModal(form)}>
                      {form.project_id ? 'Update Mapping' : 'Map to Project'}
                    </Button>
                    <Button
                      displayType="secondary"
                      onClick={() => handleToggleActive(form)}
                      disabled={isSaving}>
                      {form.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      displayType="secondary"
                      onClick={() => handleRegenerate(form)}
                      disabled={isSaving}>
                      Regenerate Token
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <NoDataFound disabledBtn={true} screenHeight={false} />
          )}
        </div>
      </div>

      {openCreateModal && (
        <Modal
          isDirty={true}
          width={50}
          name="Add Website Form"
          onClose={() => {
            setOpenCreateModal(false);
            setNewFormName('');
          }}>
          <div className="flex flex-col gap-2">
            <p>Form name</p>
            <input
              className="w-full p-2 border-[1px] border-stone-300 rounded"
              placeholder="e.g. Framer Contact Form"
              value={newFormName}
              onChange={(e) => setNewFormName(e.target.value)}
            />
          </div>
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              displayType="secondary"
              onClick={() => {
                setOpenCreateModal(false);
                setNewFormName('');
              }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} isLoading={isCreating} disabled={isCreating}>
              Create
            </Button>
          </div>
        </Modal>
      )}

      {openMapModal && (
        <Modal
          isDirty={true}
          width={60}
          name="Map Website Form"
          onClose={() => {
            setOpenMapModal(false);
            setSourceId(null);
            setMapData({ project_id: '', sales_ids: [] });
            setSelectedSalesPersons([]);
          }}>
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <p>Project</p>
              <select
                className="w-full p-2 border-[1px] border-stone-300 rounded"
                value={mapData.project_id}
                onChange={(e) => {
                  setMapData((prev) => ({ ...prev, project_id: e.target.value, sales_ids: [] }));
                  setSelectedSalesPersons([]);
                }}>
                <option value="" disabled>
                  Select a project
                </option>
                {projects?.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <p>Sales</p>
              <Multiselect
                options={sales?.data || []}
                selectedValues={mapData.project_id ? selectedSalesPersons : []}
                onSelect={(selectedList) => {
                  setSelectedSalesPersons(selectedList);
                  setMapData((prev) => ({
                    ...prev,
                    sales_ids: selectedList.map((person) => person.id)
                  }));
                }}
                onRemove={(selectedList) => {
                  setSelectedSalesPersons(selectedList);
                  setMapData((prev) => ({
                    ...prev,
                    sales_ids: selectedList.map((person) => person.id)
                  }));
                }}
                displayValue="name"
                placeholder="Select sales persons"
                style={{
                  chips: { background: '#2A565E', color: 'white' },
                  searchBox: {
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px',
                    background: 'white',
                    color: '#333'
                  }
                }}
              />
            </div>
          </div>

          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              displayType="secondary"
              onClick={() => {
                setOpenMapModal(false);
                setSourceId(null);
              }}>
              Cancel
            </Button>
            <Button onClick={handleSubmitMap} isLoading={isSaving} disabled={isSaving}>
              Submit
            </Button>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default WebsiteForms;
